export type UserRole = 'administrator' | 'user' | 'view_only';
export type UserStatus = 'active' | 'inactive';

export interface UserPermissions {
  manageUsers: boolean;
  manageCompanies: boolean;
  manageIntegrations: boolean;
  configureAutomation: boolean;
  configureAI: boolean;
  viewReports: boolean;
  manageAccounts: boolean;
  sendCommunications: boolean;
  logActivities: boolean;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: UserRole;
  status: UserStatus;
  avatar?: string;
  companies: string[]; // company IDs
  permissions: UserPermissions;
  lastLogin?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateUserFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  role: UserRole;
  companies: string[];
  permissions: UserPermissions;
}

export interface UserFilters {
  search: string;
  role: UserRole | 'all';
  status: UserStatus | 'all';
  sortBy: 'name_asc' | 'name_desc' | 'recent' | 'role';
}

export const DEFAULT_PERMISSIONS: Record<UserRole, UserPermissions> = {
  administrator: {
    manageUsers: true,
    manageCompanies: true,
    manageIntegrations: true,
    configureAutomation: true,
    configureAI: true,
    viewReports: true,
    manageAccounts: true,
    sendCommunications: true,
    logActivities: true,
  },
  user: {
    manageUsers: false,
    manageCompanies: false,
    manageIntegrations: false,
    configureAutomation: false,
    configureAI: false,
    viewReports: true,
    manageAccounts: true,
    sendCommunications: true,
    logActivities: true,
  },
  view_only: {
    manageUsers: false,
    manageCompanies: false,
    manageIntegrations: false,
    configureAutomation: false,
    configureAI: false,
    viewReports: true,
    manageAccounts: false,
    sendCommunications: false,
    logActivities: false,
  },
};

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  administrator: 'Full system access including user management, company settings, and all configurations',
  user: 'Can manage assigned accounts, send communications, and view reports',
  view_only: 'Read-only access to assigned accounts, communications, and reports',
};

export const ROLE_LABELS: Record<UserRole, string> = {
  administrator: 'Administrator',
  user: 'User',
  view_only: 'View Only',
};
